import React from 'react';
import { CircleX, TriangleAlert } from 'lucide-react';
import Block from './Block';
import { Badge } from '../../components/ui/badge';
import { ChartLegend, ChartLegendItem } from '../../components/ui/chart';
import { Tooltip } from '../../components/ui/tooltip';

import { fmt } from './CausalEffectBlock';

// Ranked comparison of several continuous treatments on one outcome, drawn as a
// forest plot: bar from zero to the estimate, 95% CI whisker, shared axis.
// Props: blockData = causal_comparison payload from tableyze.causal.reporting.

const GRADE_VARIANT = {
  high: 'success', good: 'success', medium: 'warning', low: 'destructive',
};

const GradeBadge = ({ grade }) => {
  if (!grade || !GRADE_VARIANT[grade]) return null;
  return (
    <Badge variant={GRADE_VARIANT[grade]} style={{ textTransform: 'capitalize' }} title={`${grade} confidence`}>
      <span aria-hidden className="ui-badge__dot" />
      {grade}
    </Badge>
  );
};

// Round-number ticks spanning [lo, hi], always including zero.
function niceTicks(lo, hi, count = 4) {
  const span = hi - lo || 1;
  const raw = span / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) || raw;
  const ticks = [];
  for (let t = Math.ceil(lo / step) * step; t <= hi + 1e-9; t += step) ticks.push(Number(t.toFixed(10)));
  if (!ticks.some((t) => Math.abs(t) < 1e-9)) ticks.push(0);
  return ticks.sort((a, b) => a - b);
}

const tickLabel = (t, scale) => {
  const v = t * scale;
  return Number.isInteger(v) ? String(v) : String(Number(v.toPrecision(3)));
};

const Track = ({ children, ticks, pct }) => (
  <div className="q-forest__track">
    {ticks.map((t) => (
      <span
        key={t}
        aria-hidden
        className={Math.abs(t) < 1e-9 ? 'q-forest__zero' : 'q-forest__grid'}
        style={{ left: pct(t) }}
      />
    ))}
    {children}
  </div>
);

const ChannelRow = ({ ch, scale, pct, ticks }) => {
  const zero = pct(0);
  const barLeft = ch.point >= 0 ? zero : pct(ch.point);
  const barWidth = `calc(${pct(Math.max(ch.point, 0))} - ${pct(Math.min(ch.point, 0))})`;
  const severe = ch.identification && ch.identification.severity === 'severe';

  return (
    <div className="q-forest__row">
      <div className="q-forest__label">
        <span className="q-truncate" title={ch.treatment}>{ch.treatment}</span>
        {severe && (
          <Tooltip content={ch.identification.message || 'Weakly identified effect — treat its size and rank with caution.'}>
            <span tabIndex={0} className="q-forest__warn" aria-label="Weakly identified effect">
              <TriangleAlert aria-hidden />
            </span>
          </Tooltip>
        )}
      </div>

      <Track ticks={ticks} pct={pct}>
        <div
          className={ch.significant ? 'q-forest__bar' : 'q-forest__bar q-forest__bar--ns'}
          style={{ left: barLeft, width: barWidth }}
          title={ch.significant ? undefined : 'Not statistically significant (95% CI includes 0)'}
        />
        <div
          className="q-forest__ci"
          style={{ left: pct(ch.ci_low), width: `calc(${pct(ch.ci_high)} - ${pct(ch.ci_low)})` }}
        />
        <div className="q-forest__point" style={{ left: pct(ch.point) }} />
      </Track>

      <div className="q-forest__value">
        <span className={ch.significant ? 'q-forest__est' : 'q-forest__est q-muted'}>
          {ch.point > 0 ? '+' : ''}{fmt(ch.point, scale)}
        </span>
        <span className="q-forest__ci-text">
          {fmt(ch.ci_low, scale)} to {fmt(ch.ci_high, scale)}
        </span>
      </div>

      <div className="q-forest__grade">
        {ch.significant ? <GradeBadge grade={ch.grade} /> : <Badge variant="outline">n.s.</Badge>}
      </div>
    </div>
  );
};

const CausalComparisonBlock = ({ blockData }) => {
  const data = blockData || {};
  const scale = data.display_scale || 1;
  const channels = data.channels || [];
  const ok = channels.filter((c) => c.status === 'complete');
  const failed = channels.filter((c) => c.status === 'error');
  const outcomeCol = (data.outcome || {}).column;

  // Shared axis across all bars, from the most-negative CI (or 0) to the
  // most-positive CI (or 0), padded so zero never sits flush on an edge.
  const rawLo = Math.min(0, ...ok.map((c) => c.ci_low));
  const rawHi = Math.max(0, ...ok.map((c) => c.ci_high));
  const pad = ((rawHi - rawLo) || 1) * 0.06;
  const lo = rawLo - pad;
  const hi = rawHi + pad;
  const span = hi - lo || 1;
  const pct = (v) => `${((v - lo) / span) * 100}%`;
  const ticks = niceTicks(lo, hi).filter((t) => t >= lo && t <= hi);
  const anySevere = data.worst_identification === 'severe';
  const anyNs = ok.some((c) => !c.significant);

  return (
    <Block
      icon="git-compare-arrows"
      title="Treatment comparison"
      description={outcomeCol ? (data.units || `Effect on ${outcomeCol}`) : null}
      footer={(
        <span>
          Bars run from zero to the estimated effect; whiskers show the 95% confidence interval.
          {anyNs && ' Hatched bars marked n.s. are not statistically significant.'}
          {anySevere && ' ⚠ marks a weakly identified effect — treat its size and rank with caution.'}
        </span>
      )}
    >
      {ok.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <div className="q-forest">
            <div className="q-forest__row q-forest__row--head">
              <span>Treatment</span>
              <span />
              <span style={{ textAlign: 'right' }}>Effect · 95% CI</span>
              <span style={{ textAlign: 'right' }}>Confidence</span>
            </div>
            {ok.map((ch) => (
              <ChannelRow key={ch.treatment} ch={ch} scale={scale} pct={pct} ticks={ticks} />
            ))}
            <div className="q-forest__row q-forest__row--axis">
              <span />
              <div className="q-forest__axis">
                {ticks.map((t) => (
                  <span key={t} style={{ left: pct(t) }}>{tickLabel(t, scale)}</span>
                ))}
              </div>
              <span />
              <span />
            </div>
          </div>
        </div>
      )}

      <ChartLegend style={{ justifyContent: 'flex-start', marginTop: 14 }}>
        <ChartLegendItem color="color-mix(in srgb, var(--chart-1) 22%, transparent)">Estimated effect</ChartLegendItem>
        <ChartLegendItem color="var(--foreground)" shape="dot">Point estimate</ChartLegendItem>
        <ChartLegendItem color="var(--foreground)" shape="line">95% CI</ChartLegendItem>
        <ChartLegendItem color="var(--muted-foreground)" shape="dashed">No effect (0)</ChartLegendItem>
        {anyNs && <ChartLegendItem color="var(--border)">Not significant</ChartLegendItem>}
      </ChartLegend>

      {failed.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 14 }}>
          {failed.map((c) => (
            <div key={c.treatment} className="q-alert q-alert--destructive">
              <CircleX aria-hidden />
              <span>
                <strong>{c.treatment}</strong>: could not estimate: {c.error}
              </span>
            </div>
          ))}
        </div>
      )}
    </Block>
  );
};

export default CausalComparisonBlock;
